import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { GameEvent } from '@tigrinho/engine';
import { BOARD_SIZE, SQUARES, baseRent, magnetSquares, mstRoundsLeft, priceOf } from '@tigrinho/engine';
import { buildingArt, pawnArt, squareIcon } from '../lib/art';
import { GROUP_COLORS, LEVEL_NAMES, SEAT_COLORS, SEAT_INKS, SEAT_TINTS } from '../lib/content';
import { moneyParts } from '../lib/format';
import { prefs } from '../lib/storage';
import { EVADE_MS, MAGNET_MS, STEP_MS } from '../net/useRoom';
import { useRoomCtx } from '../screens/RoomScreen';
import { type Layout, GRID_UNITS, cellCenterUnits, cellPoint, cellPos, fitBoard, project } from './geometry';
import { asState, effectsOn } from './selectors';
import { useCelebrating } from './useCelebrating';

interface Props {
  onSquare: (i: number) => void;
  /** Casas que podem ser escolhidas agora (Volta ao Mundo, Campeonato, cartas). */
  pickable?: Set<number> | null;
  highlight?: number | null;
  /** Balão em cima do peão de quem está decidindo. */
  bubble?: { player: string; text: string } | null;
  children: (layout: Layout) => ReactNode;
}

export function Board({ onSquare, pickable, highlight, bubble, children }: Props) {
  const stage = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState<Layout | null>(null);

  useLayoutEffect(() => {
    const el = stage.current;
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setLayout(fitBoard(r.width, r.height));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div className="stage" ref={stage}>
      {layout && (
        <>
          <Plane layout={layout} onSquare={onSquare} pickable={pickable ?? null} highlight={highlight ?? null} />
          <Props3D layout={layout} />
          <PawnLayer layout={layout} bubble={bubble ?? null} />
          <div
            className={`hud hud-${layout.view.mode}`}
            style={hudBox(layout)}
          >
            {children(layout)}
          </div>
        </>
      )}
    </div>
  );
}

/** Caixa do centro do tabuleiro (onde ficam relógio, dados e botões), sem inclinação. */
function hudBox(l: Layout): React.CSSProperties {
  const cos = Math.cos((l.view.tilt * Math.PI) / 180);
  const innerX = (GRID_UNITS - 3) * l.ux; // campo verde
  const innerY = (GRID_UNITS - 3) * l.uy;
  // No losango, o maior retângulo que cabe no campo tem metade da largura e da altura dele.
  const w = l.view.mode === 'iso' ? innerX * Math.SQRT2 * 0.56 : innerX * 0.94;
  const h = l.view.mode === 'iso' ? innerX * Math.SQRT2 * cos * 0.6 : innerY * cos * 0.94;
  return { left: l.cx - w / 2, top: l.cy - h / 2, width: w, height: h, '--u': `${l.unit}px` } as React.CSSProperties;
}

// ---------------------------------------------------------------------------
// A mesa: casas desenhadas no plano inclinado.

function Plane({
  layout,
  onSquare,
  pickable,
  highlight,
}: {
  layout: Layout;
  onSquare: (i: number) => void;
  pickable: Set<number> | null;
  highlight: number | null;
}) {
  const { room, square, seatIndex } = useRoomCtx();
  const g = room.game!;
  const { view, w, h, ux, uy, unit, cx, cy } = layout;
  // Na casa de carta o nome completo ("F total ou Legaaaal") não cabe numa linha.
  const tileName = (i: number) => (SQUARES[i]!.kind === 'chance' ? 'Legaaaal?' : square(i));
  const transform = `translate(-50%, -50%) rotateX(${view.tilt}deg) rotateZ(${view.spin}deg)`;
  const slab = `translate(-50%, -50%) translateY(${w * (view.mode === 'iso' ? 0.035 : 0.02)}px) rotateX(${view.tilt}deg) rotateZ(${view.spin}deg)`;
  const small = Math.min(ux, uy) < 46;
  // Ímã do Azarão: a cidade dele puxa quem para na casa de antes.
  const magnets = new Set(magnetSquares(g));
  // Aviso do MST: a cidade parada mostra quantas rodadas faltam (só nas últimas 3).
  const mstLeft = (i: number) => {
    const left = mstRoundsLeft(g, i);
    return left !== null && left <= 3 ? <span className="tag tag-mst">MST em {left}</span> : null;
  };

  return (
    <>
      <div className="slab" style={{ left: cx, top: cy, width: w, height: h, transform: slab }} aria-hidden="true" />
      <div
        className={`plane plane-${view.mode} ${small ? 'is-small' : ''}`}
        role="grid"
        aria-label="Tabuleiro"
        style={{ left: cx, top: cy, width: w, height: h, transform, '--u': `${unit}px`, '--ux': `${ux}px`, '--uy': `${uy}px` } as React.CSSProperties}
      >
        <div className="field" aria-hidden="true" />
        {SQUARES.map((sq) => {
          const { row, col, side } = cellPos(sq.index);
          const o = g.props[sq.index];
          const ownerIdx = o ? seatIndex(o.owner) : -1;
          const fx = effectsOn(g, sq.index);
          const festival = g.festivals.includes(sq.index);
          const champ = g.championship?.square === sq.index ? g.championship.mult : 0;
          const ownable = sq.kind === 'city' || sq.kind === 'resort';
          const rent = o ? baseRent(asState(g), sq.index) : 0;
          const label = [
            square(sq.index),
            o ? `de ${room.seats[ownerIdx]?.name}` : ownable ? 'livre' : '',
            o && sq.kind === 'city' ? LEVEL_NAMES[o.level] : '',
          ]
            .filter(Boolean)
            .join(', ');
          return (
            <button
              key={sq.index}
              id={`cell-${sq.index}`}
              className={[
                'cell',
                `cell-${side}`,
                `kind-${sq.kind}`,
                o ? 'is-owned' : '',
                highlight === sq.index ? 'is-hot' : '',
                pickable?.has(sq.index) ? 'is-pickable' : '',
                pickable && !pickable.has(sq.index) ? 'is-dim' : '',
              ].join(' ')}
              style={
                {
                  gridRow: row,
                  gridColumn: col,
                  '--band': sq.group ? GROUP_COLORS[sq.group] : 'transparent',
                  '--owner': ownerIdx >= 0 ? SEAT_COLORS[ownerIdx] : 'transparent',
                  '--owner-tint': ownerIdx >= 0 ? SEAT_TINTS[ownerIdx] : '#fffaf0',
                  '--owner-ink': ownerIdx >= 0 ? SEAT_INKS[ownerIdx] : '#fff',
                } as React.CSSProperties
              }
              onClick={() => onSquare(sq.index)}
              aria-label={label}
            >
              <span className={`face face-${side} ${festival ? 'is-event' : ''}`} aria-hidden="true">
                {sq.kind === 'city' && <span className="band" />}
                <span className="cell-name" style={{ '--len': tileName(sq.index).length } as React.CSSProperties}>
                  {tileName(sq.index)}
                </span>
                {ownable && <Price n={o ? rent : priceOf(sq.index)} owned={!!o} />}
                {festival && <span className="tag tag-festival">★ Evento</span>}
                {champ > 0 && <span className="tag tag-champ">Copa x{champ}</span>}
                {fx.length > 0 && <span className="tag tag-fx">Interditada</span>}
                {(g.farms ?? []).includes(sq.index) && <span className="tag tag-farm">Fazendeiro</span>}
                {magnets.has(sq.index) && <span className="tag tag-magnet">🧲 Ímã</span>}
                {mstLeft(sq.index)}
              </span>
            </button>
          );
        })}
      </div>
    </>
  );
}

function Price({ n, owned }: { n: number; owned: boolean }) {
  const { value, unit } = moneyParts(n);
  return (
    <span className={`cell-price ${owned ? 'is-rent' : ''}`}>
      <b>{value}</b>
      <small>{unit}</small>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Objetos em pé sobre a mesa: cantos, resorts, prédios e bandeirolas.

function Props3D({ layout }: { layout: Layout }) {
  const { room, seatIndex } = useRoomCtx();
  const g = room.game!;
  const u = layout.unit;
  const items: ReactNode[] = [];

  for (const sq of SQUARES) {
    const i = sq.index;
    const o = g.props[i];
    const icon = squareIcon(i);
    if (sq.kind === 'city') {
      if (!o) continue;
      const color = SEAT_COLORS[seatIndex(o.owner)]!;
      const base = cellPoint(i, 0.2);
      if (o.level === 0) {
        const p = project(layout, base.x, base.y);
        items.push(<Flag key={`b${i}`} x={p.x} y={p.y} h={u * 0.5} color={color} />);
      } else if (o.level === 4) {
        const p = project(layout, base.x, base.y);
        items.push(
          <img key={`h${i}`} className="building is-hotel" src={buildingArt(sq.group!, 4)} alt="" draggable={false} style={sprite(p, u * 0.95, color)} />,
        );
      } else {
        // Como no Business Tour: dá para contar as casinhas (1, 2 ou 3), lado a lado na casa.
        const side = cellPos(i).side;
        const along = side === 'left' || side === 'right' ? 'y' : 'x';
        for (let k = 0; k < o.level; k++) {
          const off = (k - (o.level - 1) / 2) * 0.32;
          const pt = along === 'x' ? { x: base.x + off, y: base.y } : { x: base.x, y: base.y + off };
          const p = project(layout, pt.x, pt.y);
          items.push(
            <img key={`c${i}-${k}`} className="building is-house" src={buildingArt(sq.group!, 1)} alt="" draggable={false} style={sprite(p, u * 0.62, color)} />,
          );
        }
      }
      continue;
    }
    if (!icon) continue;
    const corner = cellPos(i).side === 'corner';
    const p = project(layout, ...xy(corner ? cellCenterUnits(i) : cellPoint(i, 0.12)));
    const h = corner ? u * 1.05 : u * 0.6;
    items.push(
      <span key={`s${i}`} id={`square-${i}`} className={`prop-sprite ${corner ? 'is-corner' : ''}`} style={sprite(p, h)}>
        <img src={icon} alt="" draggable={false} />
        {o && <i className="owner-dot" style={{ background: SEAT_COLORS[seatIndex(o.owner)] }} />}
      </span>,
    );
  }
  return (
    <div className="props-layer" aria-hidden="true">
      {items}
    </div>
  );
}

const xy = (p: { x: number; y: number }): [number, number] => [p.x, p.y];

/** Posição de um objeto em pé: os "pés" no ponto da mesa, ordem de desenho pela altura na tela. */
function sprite(p: { x: number; y: number }, h: number, color?: string): React.CSSProperties {
  return {
    left: p.x,
    top: p.y,
    height: h,
    zIndex: Math.round(p.y),
    ...(color ? ({ '--owner': color } as React.CSSProperties) : {}),
  };
}

/** Bandeirola na cor do dono. */
function Flag({ x, y, h, color }: { x: number; y: number; h: number; color: string }) {
  return (
    <svg className="flag" viewBox="0 0 40 60" style={{ left: x, top: y, height: h, zIndex: Math.round(y) }} aria-hidden="true">
      <defs>
        <linearGradient id="flag-shade" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor="#fff" stopOpacity=".35" />
          <stop offset=".55" stopColor="#fff" stopOpacity="0" />
          <stop offset="1" stopColor="#000" stopOpacity=".18" />
        </linearGradient>
      </defs>
      <ellipse cx="9" cy="57" rx="8" ry="2.6" fill="rgba(0,0,0,.22)" />
      <rect x="7.2" y="6" width="3.6" height="51" rx="1.8" fill="#6b4b2a" />
      <circle cx="9" cy="5.5" r="4" fill="#ffd43b" stroke="#c99a00" strokeWidth="1" />
      <path d="M11 9 C 20 6, 27 13, 38 9 L 38 31 C 27 35, 20 28, 11 31 Z" fill={color} stroke="#fff" strokeWidth="2" strokeLinejoin="round" />
      <path d="M11 9 C 20 6, 27 13, 38 9 L 38 31 C 27 35, 20 28, 11 31 Z" fill="url(#flag-shade)" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Peões: andam casa a casa, vão presos para a Ilha e voam no Disco Voador.



type Hop = { to: number; fly: boolean; pull?: boolean; wait?: number; magnet?: { square: number; pulled: boolean } };

function PawnLayer({ layout, bubble }: { layout: Layout; bubble: { player: string; text: string } | null }) {
  const { room, bus, seatIndex } = useRoomCtx();
  const g = room.game!;
  const [pos, setPos] = useState<Record<string, { at: number; fly: boolean }>>(() =>
    Object.fromEntries(g.players.map((p) => [p.id, { at: p.position, fly: false }])),
  );
  const queues = useRef<Record<string, Hop[]>>({});
  const [magnet, setMagnet] = useState<{ player: string; square: number; pulled: boolean; at: number } | null>(null);
  const running = useRef<Record<string, boolean>>({});
  const els = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    const run = (id: string) => {
      if (running.current[id]) return;
      running.current[id] = true;
      const tick = () => {
        const hop = queues.current[id]?.shift();
        if (!hop) {
          running.current[id] = false;
          return;
        }
        // Pausa (dados da sonegação rolando antes do veredito).
        if (hop.wait) {
          setTimeout(tick, hop.wait);
          return;
        }
        // Dado especial do ímã: o peão espera o dado mostrar se vai ser puxado.
        if (hop.magnet) {
          setMagnet({ player: id, square: hop.magnet.square, pulled: hop.magnet.pulled, at: Date.now() });
          setTimeout(() => setMagnet((m) => (m?.player === id ? null : m)), MAGNET_MS + (hop.magnet.pulled ? 700 : 0));
          setTimeout(tick, MAGNET_MS);
          return;
        }
        setPos((p) => ({ ...p, [id]: { at: hop.to, fly: hop.fly || !!hop.pull } }));
        const el = els.current[id]?.firstElementChild as HTMLElement | null;
        if (el && !prefs.reducedEffects()) {
          el.animate(
            hop.pull
              ? [{ transform: 'scaleX(1) rotate(0)' }, { transform: 'scaleX(1.25) rotate(-12deg)', offset: 0.35 }, { transform: 'scaleX(.9) rotate(6deg)', offset: 0.75 }, { transform: 'none' }]
              : hop.fly
              ? [{ transform: 'translateY(0)' }, { transform: 'translateY(-60%) scale(1.15)' }, { transform: 'translateY(0)' }]
              : [{ transform: 'translateY(0)' }, { transform: 'translateY(-26%)' }, { transform: 'translateY(0)' }],
            { duration: hop.pull ? 680 : hop.fly ? 680 : STEP_MS, easing: 'ease-out' },
          );
        }
        setTimeout(tick, hop.fly || hop.pull ? 700 : STEP_MS);
      };
      tick();
    };

    const enqueue = (id: string, hops: Hop[]) => {
      const reduced = prefs.reducedEffects();
      (queues.current[id] ??= []).push(...(reduced && hops.length > 1 ? [hops[hops.length - 1]!] : hops));
      run(id);
    };

    return bus.onEvents(({ events }) => {
      for (const e of events as GameEvent[]) {
        if (e.type === 'moved') {
          if (e.how === 'walk' || e.how === 'back') {
            const dir = e.how === 'walk' ? 1 : -1;
            const hops: Hop[] = [];
            let i = e.from;
            for (let guard = 0; i !== e.to && guard < BOARD_SIZE; guard++) {
              i = (i + dir + BOARD_SIZE) % BOARD_SIZE;
              hops.push({ to: i, fly: false });
            }
            enqueue(e.player, hops);
          } else if (e.how === 'magnet') {
            enqueue(e.player, [{ to: e.to, fly: false, pull: true }]);
          } else {
            enqueue(e.player, [{ to: e.to, fly: true }]);
          }
        } else if (e.type === 'taxEvasion') {
          enqueue(e.player, [{ to: -1, fly: false, wait: EVADE_MS }]);
        } else if (e.type === 'magnetRolled') {
          enqueue(e.player, [{ to: e.from, fly: false, magnet: { square: e.square, pulled: e.pulled } }]);
        } else if (e.type === 'traveled') {
          enqueue(e.player, [{ to: e.to, fly: true }]);
        }
      }
    });
  }, [bus]);

  // Acerta quem mudou de casa sem evento de movimento (troca de lugar, reconexão).
  const positions = g.players.map((p) => `${p.id}:${p.position}`).join(',');
  useEffect(() => {
    setPos((old) => {
      let changed = false;
      const next = { ...old };
      for (const p of g.players) {
        if (running.current[p.id] || queues.current[p.id]?.length) continue;
        if (next[p.id]?.at !== p.position) {
          next[p.id] = { at: p.position, fly: true };
          changed = true;
        }
      }
      return changed ? next : old;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positions]);

  const alive = g.players.filter((p) => !p.eliminated);
  // Vários peões na mesma casa ficam lado a lado.
  const bySquare = new Map<number, string[]>();
  for (const p of alive) {
    const at = pos[p.id]?.at ?? p.position;
    bySquare.set(at, [...(bySquare.get(at) ?? []), p.id]);
  }
  const current = g.players[g.current]?.id;
  const party = useCelebrating();
  const u = layout.unit;
  const stageW = layout.cx * 2;

  return (
    <div className="pawn-layer" aria-hidden="true">
      {alive.map((p) => {
        const at = pos[p.id]?.at ?? p.position;
        const here = bySquare.get(at) ?? [p.id];
        const k = here.indexOf(p.id);
        const c = cellCenterUnits(at);
        const spread = here.length > 1 ? (k - (here.length - 1) / 2) * 0.34 : 0;
        const pt = project(layout, c.x + spread, c.y + spread * 0.5);
        const seat = room.seats.find((s) => s.id === p.id);
        const jailed = p.inJail && at === 8;
        const talk = bubble?.player === p.id ? bubble.text : null;
        return (
          <div
            key={p.id}
            ref={(el) => {
              els.current[p.id] = el;
            }}
            id={`pawn-${p.id}`}
            className={[
              'pawn',
              p.id === current ? 'is-turn' : '',
              pos[p.id]?.fly ? 'is-flying' : '',
              jailed ? 'is-jailed' : '',
              party.has(p.id) ? 'is-celebrating' : '',
            ].join(' ')}
            style={
              {
                left: pt.x,
                top: pt.y,
                height: u * 0.78,
                zIndex: 2000 + Math.round(pt.y),
                '--seat': SEAT_COLORS[seatIndex(p.id)],
              } as React.CSSProperties
            }
          >
            <div className="pawn-body">
              <i className="pawn-base" />
              <img src={pawnArt(seat?.pawn ?? 0)[party.has(p.id) ? 'happy' : 'idle']} alt="" draggable={false} />
            </div>
            {talk && (
              <span
                className={`speech ${pt.x > stageW * 0.72 ? 'is-right' : pt.x < stageW * 0.28 ? 'is-left' : ''} ${pt.y < u * 2.2 ? 'is-below' : ''}`}
              >
                {talk}
              </span>
            )}
          </div>
        );
      })}
      {magnet && <MagnetDie layout={layout} at={pos[magnet.player]?.at ?? 0} pulled={magnet.pulled} key={magnet.at} />}
    </div>
  );
}

/** Dado especial do Ímã do Azarão: metade das faces é ímã (puxa), metade é mão (não puxa). */
function MagnetDie({ layout, at, pulled }: { layout: Layout; at: number; pulled: boolean }) {
  const [done, setDone] = useState(false);
  const [face, setFace] = useState(0);
  useEffect(() => {
    const spin = setInterval(() => setFace((f) => f + 1), 90);
    const stop = setTimeout(() => {
      clearInterval(spin);
      setDone(true);
    }, MAGNET_MS * 0.62);
    return () => {
      clearInterval(spin);
      clearTimeout(stop);
    };
  }, []);
  const c = cellCenterUnits(at);
  const p = project(layout, c.x, c.y);
  const u = layout.unit;
  const show = done ? pulled : face % 2 === 0;
  return (
    <div className={`magnet-die ${done ? 'is-done' : ''} ${done && pulled ? 'is-pulled' : ''}`} style={{ left: p.x, top: p.y - u * 0.95, zIndex: 9000 }}>
      <span className="magnet-face">{show ? '🧲' : '✋'}</span>
      {done && <b>{pulled ? 'Puxou!' : 'Escapou!'}</b>}
    </div>
  );
}
